/**
 * BlogHero - Reusable Hero Bar Component for Blog Pages
 *
 * A unified hero bar component with hexagon pattern overlay,
 * navigation breadcrumbs, and optional hero image support.
 * Used by both Blog index and BlogPost pages.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import HexagonLogo from './HexagonLogo';
import './BlogHero.css';

const BlogHero = ({
  // Breadcrumb configuration
  breadcrumbs = [{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }],
  currentPage = null,

  // Navigation links on the right
  navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Search', to: '/search' },
    { label: 'Text Search', to: '/text-search' }
  ],

  // Hero image (optional - for article pages)
  heroImage = null,
  heroAlt = '',

  // Whether to show pattern overlay on hero image
  showPattern = true,

  // Additional class names
  className = ''
}) => {
  const hasHeroImage = !!heroImage;

  return (
    <div className={`blog-hero ${hasHeroImage ? 'blog-hero-with-image' : ''} ${className}`}>
      {/* Navigation Bar */}
      <nav className="blog-hero-nav">
        <div className="blog-hero-nav-left">
          <Link to="/" className="blog-hero-logo" title="Fantomize Home">
            <HexagonLogo size={32} strokeWidth={2.5} />
          </Link>
          <div className="blog-hero-breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.to || index}>
                {crumb.to ? (
                  <Link to={crumb.to}>{crumb.label}</Link>
                ) : (
                  <span className="breadcrumb-current">{crumb.label}</span>
                )}
                {index < breadcrumbs.length - 1 && (
                  <span className="breadcrumb-sep">/</span>
                )}
              </React.Fragment>
            ))}
            {currentPage && (
              <>
                <span className="breadcrumb-sep">/</span>
                <span className="breadcrumb-current">{currentPage}</span>
              </>
            )}
          </div>
        </div>
        <div className="blog-hero-nav-links">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to}>{link.label}</Link>
          ))}
        </div>
      </nav>

      {/* Hero Image with Pattern Overlay */}
      {hasHeroImage && (
        <div className="blog-hero-image-container">
          <img
            src={heroImage}
            alt={heroAlt}
            className="blog-hero-image"
          />
          <div className="blog-hero-overlay">
            {showPattern && <div className="blog-hero-pattern" />}
          </div>
        </div>
      )}

      {/* Pattern-only hero bar (no image) */}
      {!hasHeroImage && showPattern && (
        <div className="blog-hero-pattern-bar" />
      )}
    </div>
  );
};

export default BlogHero;


